import { useEffect, useState } from "react";
import locationIcon from "../assets/images/location.png";
import group from "../assets/images/restaurants/user-group.png";
import search from "../assets/images/restaurants/search.png";
import arrow from "../assets/images/order/orange-arrow.png";
import star from "../assets/images/navbar/star.png";
import RestaurantsItemCard from "../components/RestaurantsItemCard";
import SigninModal from "../modal/SigninModal";
import Otp from "../modal/Otp";
import Welcome from "../modal/Welcome";
import GroupModal from "../modal/GroupModal";
import { RestaurantI, RestaurentsItemCategoryI, Dish } from "../utils/types";
import { restaurantService, favoriteService, orderService, CouponData } from "../services";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import defaultImage from "../assets/images/cuisines/default.jpeg";
import toast from "react-hot-toast";
import { persistPendingCoupon } from "../utils/helpers";

const RestaurantsDetails = () => {
  const [activeTab, setActiveTab] = useState<string>("All");
  const [isSignInModalOpen, setIsSignInModalOpen] = useState<boolean>(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState<boolean>(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState<boolean>(false);
  const [isGroupOpen, setIsGroupOpen] = useState<boolean>(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [restaurant, setRestaurant] = useState<RestaurantI | null>(null);
  const { checkAuth, openSigninModal } = useAuth();
  const [itemCategories, setItemCategories] =
    useState<RestaurentsItemCategoryI | null>(null);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [loadingDishes, setLoadingDishes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [searchResultCategories, setSearchResultCategories] = useState<Map<string, number>>(new Map());
  const [groupItems, setGroupItems] = useState<any[]>([]);
  const { id } = useParams<{ id: string }>(); // Get restaurant ID from URL parameters
  const navigate = useNavigate();
  const location = useLocation();
  const groupSessionId =
    (location.state as { groupSessionId?: string } | null)?.groupSessionId ||
    null;
  
  // Use cart context
  const { cartItems, getCartRestaurantId } = useCart();
  const [offers, setOffers] = useState<CouponData[]>([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const handleOrderNow = () => {
    if (groupSessionId) {
      navigate(`/group-order/${groupSessionId}`);
      return;
    }

    // Check if user is already authenticated
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    
    if (token) {
      // User is logged in, navigate directly to order summary
      // Cart data will be fetched from context in OrderSummary page
      navigate('/order-summary');
    } else {
      // User not logged in, show sign-in modal
      setIsSignInModalOpen(true);
    }
  };

  const refreshGroupItems = async () => {
    if (!groupSessionId) return;
    try {
      const res: any = await orderService.getGroupOrderSession(groupSessionId);
      if (res?.status && res?.data?.items) {
        setGroupItems(res.data.items);
      }
    } catch (err) {
      console.error("Failed to refresh group items", err);
    }
  };

  const handleGetStarted = () => {
    setIsSignInModalOpen(false);
    setIsOtpModalOpen(true);
  };

  const handleOtpVerify = () => {
    setIsOtpModalOpen(false);
    setIsWelcomeModalOpen(true);
    
    // After welcome modal, navigate to order summary
    // Cart data will be fetched from context in OrderSummary page
    setTimeout(() => {
      navigate('/order-summary');
    }, 2000); // Navigate after 2 seconds to allow user to see welcome modal
  };

  useEffect(() => {
    if (!id || id === 'undefined' || id.trim() === '') {
      setLoading(false);
      return;
    }

    const fetchRestaurants = async () => {
      try {
        setLoading(true);
        const response: any = await restaurantService.getSingleRestaurant(id);
        setRestaurant(response.data || null);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching restaurants:", err);
        setError(err.message || "Failed to load restaurants");
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, [id]);

  useEffect(() => {
    const restaurantKey = restaurant?._id || restaurant?.restaurantId || id;
    if (!restaurantKey) return;

    const fetchOffers = async () => {
      setOffersLoading(true);
      try {
        const cartSubtotal = cartItems
          .filter(
            (item: any) =>
              item.restaurantId === restaurant?._id ||
              item.restaurantId === restaurant?.restaurantId
          )
          .reduce((sum: number, item: any) => {
            const price = item.dish?.basePrice || 0;
            return sum + price * (item.quantity || 1);
          }, 0);
        const orderValue = Math.max(
          cartSubtotal,
          restaurant?.minimumOrderValue || 1
        );
        const res: any = await orderService.getApplicableCoupons(
          restaurantKey,
          orderValue
        );
        if (res?.status && Array.isArray(res.data)) {
          setOffers(res.data);
        } else {
          setOffers([]);
        }
      } catch (error) {
        console.error("Failed to fetch offers:", error);
        setOffers([]);
      } finally {
        setOffersLoading(false);
      }
    };

    fetchOffers();
  }, [restaurant?._id, restaurant?.restaurantId, restaurant?.minimumOrderValue, id, cartItems]);

  const formatOfferDiscount = (coupon: CouponData) => {
    if (coupon.discountType === "percentage") {
      return `${coupon.discountValue}% OFF`;
    }
    return `₹${coupon.discountValue} OFF`;
  };

  const handleApplyOffer = (coupon: CouponData) => {
    const restaurantKey = restaurant?._id || restaurant?.restaurantId || id;
    if (!restaurantKey) return;
    persistPendingCoupon(restaurantKey, coupon);
    if (restaurant?._id && restaurant._id !== restaurantKey) {
      persistPendingCoupon(restaurant._id, coupon);
    }
    if (restaurant?.restaurantId && restaurant.restaurantId !== restaurantKey) {
      persistPendingCoupon(restaurant.restaurantId, coupon);
    }
    setAppliedCouponCode(coupon.couponCode);
    toast.success(`${coupon.couponCode} will be applied at checkout`);
    const cartRestaurantId = getCartRestaurantId();
    if (
      cartRestaurantId &&
      (cartRestaurantId === restaurant?._id ||
        cartRestaurantId === restaurant?.restaurantId)
    ) {
      navigate("/order-summary");
    }
  };

  useEffect(() => {
    if (!restaurant?._id && !restaurant?.restaurantId) return;
    const restaurantKey = restaurant._id || restaurant.restaurantId;
    const checkFavorite = async () => {
      try {
        const res: any = await favoriteService.isFavorite(restaurantKey);
        setIsFavorite(!!(res?.data?.isFavorite ?? res?.data));
      } catch {
        setIsFavorite(false);
      }
    };
    checkFavorite();
  }, [restaurant?._id, restaurant?.restaurantId]);

  const handleToggleFavorite = async () => {
    if (!checkAuth()) {
      openSigninModal();
      return;
    }
    if (!restaurant) return;
    const restaurantKey = restaurant._id || restaurant.restaurantId;
    try {
      if (isFavorite) {
        await favoriteService.removeFavorite(restaurantKey);
        setIsFavorite(false);
        toast.success("Removed from favorites");
      } else {
        await favoriteService.addFavorite(restaurantKey);
        setIsFavorite(true);
        toast.success("Added to favorites");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to update favorites");
    }
  };

  const openRestaurantLocation = () => {
    if (!restaurant?.location) return;
    const { googleMapsLink, lat, lng, address, city } = restaurant.location;
    const geo = (restaurant as any).geoLocation?.coordinates;
    if (googleMapsLink) {
      window.open(googleMapsLink, "_blank", "noopener,noreferrer");
      return;
    }
    if (lat && lng) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    if (Array.isArray(geo) && geo.length >= 2) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${geo[1]},${geo[0]}`,
        "_blank",
        "noopener,noreferrer"
      );
      return;
    }
    const query = encodeURIComponent(
      [address, city].filter(Boolean).join(", ")
    );
    if (query) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${query}`,
        "_blank",
        "noopener,noreferrer"
      );
    } else {
      toast.error("Location not available");
    }
  };

  const callRestaurant = () => {
    const phone =
      (restaurant as any)?.phone ||
      (restaurant as any)?.contactNumber ||
      (restaurant as any)?.mobile ||
      (restaurant as any)?.ownerPhone;
    if (!phone) {
      toast.error("Restaurant phone number not available");
      return;
    }
    window.location.href = `tel:${phone}`;
  };

  useEffect(() => {
    if (groupSessionId) {
      refreshGroupItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupSessionId]);

  useEffect(() => {
    // Don't fetch if restaurant is not loaded yet
    if (!restaurant || !restaurant.restaurantId) {
      return;
    }

    const fetchRestaurentsItemsCategory = async () => {
      try {
        setLoading(true);
        const response: any =
          await restaurantService.getRestaurantsByCategory(restaurant.restaurantId);
        setItemCategories(response.data || null);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching restaurants:", err);
        setError(err.message || "Failed to load restaurants");
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurentsItemsCategory();
  }, [restaurant]);

  // Fetch dishes when active tab or search text changes
  useEffect(() => {
    const fetchDishes = async () => {
      if (!restaurant) return;

      try {
        setLoadingDishes(true);
        const categoryId = itemCategories?.items?.find(
          (item) => item.name === activeTab
        )?.id;

        const params: any = {
          restaurantId: restaurant.restaurantId,
          page: 1,
          limit: 20,
        };

        // If searching, ignore category filter (search across all categories)
        if (searchText.trim()) {
          params.searchText = searchText.trim();
        } else {
          // Only add categoryId if not "All" tab and not searching
          if (activeTab !== "All" && categoryId) {
            params.categoryId = categoryId;
          }
        }

        const response: any = await restaurantService.searchDishes(params);
        const dishesData = response.data || [];
        setDishes(dishesData);
        
        // If searching, calculate category counts from results
        if (searchText.trim()) {
          const categoryCountMap = new Map<string, number>();
          dishesData.forEach((dish: Dish) => {
            const count = categoryCountMap.get(dish.categoryId) || 0;
            categoryCountMap.set(dish.categoryId, count + 1);
          });
          setSearchResultCategories(categoryCountMap);
        } else {
          setSearchResultCategories(new Map());
        }
      } catch (err: any) {
        console.error("Error fetching dishes:", err);
        setError(err.message || "Failed to load dishes");
      } finally {
        setLoadingDishes(false);
      }
    };

    if (restaurant && itemCategories) {
      fetchDishes();
    }
  }, [activeTab, restaurant, itemCategories, searchText]);

  // // Helper function to check if restaurant is currently open
  // const isRestaurantOpen = (restaurant: RestaurantI): "open now" | "closed" => {
  //   const now = new Date();
  //   const currentDay = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"][
  //     now.getDay()
  //   ];

  //   // Check if restaurant operates today
  //   if (!restaurant.operatingDays.includes(currentDay)) {
  //     return "closed";
  //   }

  //   const currentTime = now.getHours() * 60 + now.getMinutes();
  //   const [openHour, openMin] = restaurant.operatingHours.openTime
  //     .split(":")
  //     .map(Number);
  //   const [closeHour, closeMin] = restaurant.operatingHours.closeTime
  //     .split(":")
  //     .map(Number);

  //   const openTime = openHour * 60 + openMin;
  //   let closeTime = closeHour * 60 + closeMin;

  //   // Handle case where closing time is after midnight
  //   if (closeTime < openTime) {
  //     closeTime += 24 * 60;
  //   }

  //   if (currentTime >= openTime && currentTime <= closeTime) {
  //     return "open now";
  //   }

  //   return "closed";
  // };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-white dark:bg-gray-900 transition-colors">
        <p className="text-gray-500 dark:text-gray-400">Loading restaurant details...</p>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-white dark:bg-gray-900 transition-colors">
        <div className="text-xl font-semibold dark:text-white">No items in the cart</div>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Browse Restaurants
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-white dark:bg-gray-900 transition-colors">
        <div className="text-xl font-semibold text-red-500 dark:text-red-400">{error}</div>
        <button
          onClick={() => navigate("/")}
          className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
        >
          Browse Restaurants
        </button>
      </div>
    );
  }

  // const status = isRestaurantOpen(restaurant);
  const status = restaurant.deliveryAvailable ? "open now" : "closed";
  const isOpen = status === "open now";
  const amenityChips: string[] = [];
  if (restaurant.deliveryAvailable) amenityChips.push("Delivery available");
  if (restaurant.parkingAvailable) amenityChips.push("Parking");
  if (restaurant.acAvailable === "YES") amenityChips.push("AC");
  if (restaurant.liveMusic) amenityChips.push("Live Music");
  if (restaurant.alcoholServed) amenityChips.push("Bar");
  const ratingAvg = (restaurant.rating?.averageRating ?? 0).toFixed(1);
  const ratingCount = restaurant.rating?.totalRatings ?? 0;
  const prepMins = restaurant.avgPreparationTime || 30;
  const minOrder = restaurant.minimumOrderValue ?? 0;
  const openClose = restaurant.operatingHours
    ? `${restaurant.operatingHours.openTime} - ${restaurant.operatingHours.closeTime}`
    : "";

  return (
    <div className="flex flex-col md:flex-row border-2 dark:border-gray-700 bg-[#FFFFFF] dark:bg-gray-900 relative w-full min-w-0 transition-colors duration-300">

      <div className="w-full md:w-[35%] md:min-w-0 md:max-w-[35%] md:border-r dark:border-gray-700 md:py-6">
        {/* Kitchen details — mobile-app style hero + info card */}
        <div className="relative md:px-6">
          <div className="relative h-[220px] md:h-[280px] w-full md:rounded-2xl overflow-hidden">
            <img
              src={restaurant.kitchenPhotos?.[0] || defaultImage}
              alt={restaurant.businessName}
              className="object-cover h-full w-full"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 to-transparent" />

            {/* Overlay actions */}
            <div className="absolute top-3 left-3 right-3 z-10 flex justify-between items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-10 w-10 flex-shrink-0 rounded-full bg-black/45 flex items-center justify-center"
                aria-label="Back"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() =>
                    document
                      .getElementById("restaurant-dish-search")
                      ?.focus()
                  }
                  className="h-10 w-10 flex-shrink-0 rounded-full bg-black/45 flex items-center justify-center"
                  aria-label="Search"
                >
                  <img src={search} alt="" className="h-4 w-4 invert" />
                </button>
                <button
                  type="button"
                  onClick={() => setIsGroupOpen(true)}
                  className="h-10 w-10 flex-shrink-0 rounded-full bg-black/45 flex items-center justify-center"
                  aria-label="Group order"
                >
                  <img src={group} alt="" className="h-5 w-5 invert" />
                </button>
              </div>
            </div>
          </div>

          {/* Overlapping info card */}
          <div className="relative -mt-8 md:-mt-10 mx-3 md:mx-0 bg-white dark:bg-gray-800 rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.08)] p-4 md:p-5">
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <h1 className="text-[#050505] dark:text-white font-urbanist text-[22px] md:text-[24px] font-bold leading-tight">
                  {restaurant.businessName}
                </h1>
                <span
                  className={`inline-flex mt-2 px-2.5 py-1 rounded-full text-xs font-semibold text-white ${
                    isOpen ? "bg-[#22C55E]" : "bg-[#C96B03]"
                  }`}
                >
                  {isOpen ? "Open Now" : "Closed"}
                </span>
              </div>
              <div className="flex flex-col items-end flex-shrink-0">
                <div className="inline-flex items-center gap-1 bg-[#22C55E] text-white px-2 py-1 rounded-md text-sm font-semibold">
                  <span>★</span>
                  <span>{ratingAvg}</span>
                </div>
                <span className="text-[#8A8A8A] dark:text-gray-400 text-[11px] mt-1">
                  By {ratingCount > 0 ? `${ratingCount}+` : "0"}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={openRestaurantLocation}
              className="flex items-start gap-2 mt-3 text-left w-full min-w-0"
            >
              <img
                src={locationIcon}
                alt=""
                className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-70 dark:invert"
              />
              <span className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm leading-snug line-clamp-2 min-w-0">
                {restaurant.location.address
                  ? `${restaurant.location.address}, ${restaurant.location.city}`
                  : restaurant.location.city}
              </span>
            </button>

            {/* Stats row */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#EFEFEF] dark:border-gray-700 text-sm flex-wrap">
              <span className="inline-flex items-center gap-1 text-[#22C55E] font-semibold">
                <svg width="12" height="14" viewBox="0 0 12 16" fill="none">
                  <path
                    d="M6 0L0 9h4v7l6-9H6V0z"
                    fill="#22C55E"
                  />
                </svg>
                {prepMins} mins
              </span>
              {openClose && (
                <>
                  <span className="text-[#D0D0D0]">|</span>
                  <span className="text-[#6A6A6A] dark:text-gray-400">
                    {openClose}
                  </span>
                </>
              )}
              {minOrder > 0 && (
                <>
                  <span className="text-[#D0D0D0]">|</span>
                  <span className="text-[#6A6A6A] dark:text-gray-400">
                    Min ₹{minOrder}
                  </span>
                </>
              )}
            </div>

            {/* Amenity chips — wrap so tags/icons are never clipped */}
            {amenityChips.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {amenityChips.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E0E0E0] dark:border-gray-600 text-xs text-[#050505] dark:text-gray-200 bg-white dark:bg-gray-800"
                  >
                    <span className="text-[#22C55E] font-bold">✓</span>
                    {chip}
                  </span>
                ))}
              </div>
            )}

            <div className="flex gap-2 mt-4 min-w-0">
              <button
                type="button"
                onClick={openRestaurantLocation}
                className="flex-1 min-w-0 py-2 rounded-xl border dark:border-gray-600 text-sm font-urbanist font-medium text-[#050505] dark:text-white"
              >
                Directions
              </button>
              <button
                type="button"
                onClick={callRestaurant}
                className="flex-1 min-w-0 py-2 rounded-xl border dark:border-gray-600 text-sm font-urbanist font-medium text-[#050505] dark:text-white"
              >
                Call
              </button>
              <button
                type="button"
                onClick={handleToggleFavorite}
                className="h-10 w-10 flex-shrink-0 rounded-xl border dark:border-gray-600 flex items-center justify-center"
                aria-label="Favorite"
              >
                <img
                  src={star}
                  alt=""
                  className={`h-5 w-5 ${
                    isFavorite ? "opacity-100" : "opacity-40 dark:invert"
                  }`}
                />
              </button>
            </div>

            {groupSessionId && (
              <div className="mt-3 p-3 rounded-xl bg-[#F5F5F5] dark:bg-gray-700 flex justify-between items-center gap-3">
                <p className="text-sm font-urbanist text-[#7402A7] dark:text-purple-400 font-medium">
                  Group order mode
                </p>
                <button
                  type="button"
                  onClick={() => navigate(`/group-order/${groupSessionId}`)}
                  className="text-sm font-semibold text-[#7402A7] dark:text-purple-400 whitespace-nowrap"
                >
                  Back to group
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="px-3 md:px-6 mt-5 pb-4">
          {/* ----------available offers ----------*/}
          <div>
            <h6 className="text-[#050505] dark:text-white text-left font-urbanist text-[18px] font-semibold leading-[140%]">
              Offers Available
            </h6>
            {offersLoading ? (
              <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm mt-3">
                Loading offers...
              </p>
            ) : offers.length === 0 ? (
              <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-sm mt-3">
                No offers available right now
              </p>
            ) : (
              offers.map((offer) => {
                const isApplied =
                  appliedCouponCode.toUpperCase() ===
                  offer.couponCode.toUpperCase();
                return (
                  <div
                    key={offer._id}
                    className="flex items-center max-w-2xl w-full mt-[12px] min-w-0"
                  >
                    <div className="bg-orange-500 text-white w-[35px] flex-shrink-0 h-[114px] md:h-[122px] rounded-l-2xl flex items-center justify-center">
                      <p className="rotate-[-90deg] text-[14px] font-extrabold leading-[20px] whitespace-nowrap ">
                        {formatOfferDiscount(offer)}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-r-2xl p-1 md:p-2 flex justify-between items-center transition-colors">
                      <div className="min-w-0">
                        <div className="px-[4px] py-[10px] border-b-2 dark:border-gray-600 border-dashed">
                          <h3 className="text-[#050505] dark:text-white text-left font-urbanist text-[16px] font-medium leading-[20px]">
                            {offer.couponCode}
                          </h3>
                          <p className="text-[#6A6A6A] dark:text-gray-400 text-left font-urbanist text-[12px] font-normal leading-[17px]">
                            {offer.description}
                          </p>
                        </div>
                        <p className="text-[#6A6A6A] dark:text-gray-400 text-left font-urbanist text-[11px] font-normal leading-[146%] mt-3 max-w-md">
                          Min. order ₹{offer.minOrderValue}
                          {offer.maxDiscountCap > 0
                            ? ` · Max discount ₹${offer.maxDiscountCap}`
                            : ""}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleApplyOffer(offer)}
                        className={`flex-shrink-0 font-urbanist text-[14px] md:text-[16px] font-semibold leading-[17px] -mt-[70px] md:-mt-[47px] ${
                          isApplied
                            ? "text-green-600"
                            : "text-purple-700 dark:text-purple-400"
                        }`}
                      >
                        {isApplied ? "APPLIED" : "APPLY"}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      <div className="w-full md:w-[65%] md:min-w-0 md:max-w-[65%] relative">
        {/* Search Bar */}
        <div className="px-4 md:px-8 pt-4 pb-2">
          <div className="relative">
            <img 
              src={search} 
              alt="search" 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-[20px] w-[20px] text-gray-400 dark:invert pointer-events-none"
            />
            <input
              id="restaurant-dish-search"
              type="text"
              placeholder={`Search dishes in ${restaurant.businessName}`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-12 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:text-white transition-colors"
            />
            {searchText && (
              <button
                onClick={() => setSearchText("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 h-8 w-8 flex items-center justify-center"
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          {searchText && (
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 break-words">
              Showing {dishes.length} search result{dishes.length !== 1 ? 's' : ''} for "{searchText}"
            </div>
          )}
        </div>
        
        {/* Tabs */}
        <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700 scrollbar-hide px-2 md:px-0">
          <div className="hidden md:flex justify-center items-center mx-[20px] flex-shrink-0">
            <img src={search} alt="search" className="h-[24px] w-[24px] opacity-0 dark:invert" />
          </div>
          {/* All button */}
          <button
            onClick={() => {
              setSearchText(""); // Clear search when clicking tab
              setActiveTab("All");
            }}
            className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap flex-shrink-0
              ${
                activeTab === "All" && !searchText
                  ? "text-[#7402A7] dark:text-purple-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }
              ${searchText ? "opacity-50" : ""}`}
          >
            All ({String(searchText ? dishes.length : itemCategories?.total || 0).padStart(2, "0")})
            {activeTab === "All" && !searchText && (
              <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#7402A7] dark:bg-purple-400 rounded-full"></span>
            )}
          </button>
          {itemCategories?.items
            ?.filter((item) => {
              // When searching, only show categories that have results
              if (searchText) {
                return searchResultCategories.has(item.id);
              }
              return true;
            })
            ?.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setSearchText(""); // Clear search when clicking tab
                setActiveTab(item.name);
              }}
              className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap flex-shrink-0
              ${
                activeTab === item.name && !searchText
                  ? "text-[#7402A7] dark:text-purple-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              }
              ${searchText ? "opacity-50" : ""}`}
            >
              {item.name} ({String(searchText ? (searchResultCategories.get(item.id) || 0) : item.dishCount).padStart(2, "0")})
              {activeTab === item.name && !searchText && (
                <span className="absolute left-0 bottom-0 h-[2px] w-full bg-[#7402A7] dark:bg-purple-400 rounded-full"></span>
              )}
            </button>
          ))}
        </div>

        {/* items section */}
        <div className="px-2 md:px-12 pb-32 md:pb-24">
          {loadingDishes ? (
            <div className="flex justify-center items-center py-20">
              <p className="text-gray-500 dark:text-gray-400">Loading dishes...</p>
            </div>
          ) : dishes.length === 0 ? (
            <div className="flex justify-center items-center py-20">
              <p className="text-gray-500 dark:text-gray-400">No dishes available</p>
            </div>
          ) : (
            dishes.map((dish) => {
              const groupItem = groupItems.find(
                (item: any) =>
                  item.dishId === dish._id || item.dish?._id === dish._id
              );
              return (
              <RestaurantsItemCard
                key={dish._id}
                dishId={dish._id}
                restaurantId={restaurant.restaurantId}
                restaurantName={restaurant.businessName}
                image={dish.images[0]?.url || defaultImage}
                itemName={dish.dishName}
                itemPrice={dish.comparePrice}
                discountedPrice={dish.basePrice}
                deliveryTime={`${dish.preparationTime} mins`}
                status={status}
                foodType={dish.foodType}
                groupSessionId={groupSessionId}
                groupItemId={groupItem?._id || null}
                groupQuantity={groupItem?.quantity || 0}
                onGroupItemChanged={refreshGroupItems}
              />
            );
            })
          )}
        </div>

        {((groupSessionId && groupItems.length > 0) ||
          (cartItems &&
            cartItems.some(
              (item: any) => item.restaurantId === restaurant?.restaurantId
            ))) && (
          /* Desktop Order Button - Hidden on mobile */
          <div className="hidden md:block fixed bottom-[20px] left-2/3 transform -translate-x-1/2 z-50">
            <div
              className="w-[364px] flex justify-between py-[12px] pl-[24px] pr-[12px] items-center gap-2 bg-[#7402A7] dark:bg-purple-600 text-white rounded-[150px] cursor-pointer shadow-[0 2px 12px 10px rgba(151, 57, 193, 0.15)] hover:bg-[#5a0282] dark:hover:bg-purple-700 transition-colors"
              onClick={handleOrderNow}
            >
              <p className="text-white text-center font-urbanist text-[20px] font-semibold leading-[140%]">
                {groupSessionId ? "Back to Group Order" : "Order Now"}
              </p>
              <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
                <img
                  src={arrow}
                  alt="right arrow"
                  className="h-[41px] w-[30px] stroke-[2.587px] stroke-[#FB8606]"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AddOnes Modal */}
      {/* <AddOnes
        isOpen={isAddOnesModalOpen}
        onClose={() => setIsAddOnesModalOpen(false)}
        count={Object.values(itemCounts).reduce((sum, count) => sum + count, 0)}
        onOrderNow={handleOrderNow}
      /> */}

      {/* SignIn Modal */}
      <SigninModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
        onGetStarted={handleGetStarted}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
      />

      {/* OTP Modal */}
      <Otp
        isOpen={isOtpModalOpen}
        onClose={() => setIsOtpModalOpen(false)}
        mobileNumber={mobileNumber}
        onVerify={handleOtpVerify}
      />

      {/* Welcome Modal */}
      <Welcome
        isOpen={isWelcomeModalOpen}
        onClose={() => setIsWelcomeModalOpen(false)}
      />

      {/* Group Modal */}
      {isGroupOpen && (
        <GroupModal
          onClose={() => setIsGroupOpen(false)}
          restaurantId={id || restaurant?._id || ""}
        />
      )}
    </div>
  );
};

export default RestaurantsDetails;
